import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import type { SupportTicket } from "../../types/supportTicket";
import TicketStatusBadge from "./TicketStatusBadge";
import TicketPriorityBadge from "./TicketPriorityBadge";
import { formatDateTime } from "../../utils/dateFormat";
import DataTable from "../../admin/components/DataTable";
import type { Column } from "../../admin/components/DataTable";

interface Props {
  tickets: SupportTicket[];
  detailBasePath: string;
  showSubscriber?: boolean;
}

const TicketTable: React.FC<Props> = ({
  tickets,
  detailBasePath,
  showSubscriber = false,
}) => {
  const tableRows = useMemo(
    () =>
      tickets.map((ticket) => ({
        ...ticket,
        createdAtDisplay: ticket.createdAt
          ? formatDateTime(ticket.createdAt)
          : "-",
        updatedAtDisplay: ticket.updatedAt
          ? formatDateTime(ticket.updatedAt)
          : "-",
        subscriberSearch: `${ticket.subscriberName || ""} ${
          ticket.subscriberEmail || ""
        }`,
        subjectSearch: `${ticket.subject || ""} ${ticket.service || ""}`,
      })),
    [tickets],
  );

  const columns: Column[] = useMemo(() => {
    const baseColumns: Column[] = [
      {
        key: "ticketNo",
        label: "ID",
      },
      {
        key: "subjectSearch",
        label: "Subject",
        render: (ticket: SupportTicket) => (
          <div className="ticket-subject-cell">
            <strong>{ticket.subject}</strong>
            {ticket.service && <small>{ticket.service}</small>}
          </div>
        ),
      },
    ];

    if (showSubscriber) {
      baseColumns.push({
        key: "subscriberSearch",
        label: "Subscriber",
        render: (ticket: SupportTicket) => (
          <div className="ticket-subject-cell">
            <strong>{ticket.subscriberName || "-"}</strong>
            <small>{ticket.subscriberEmail || "-"}</small>
          </div>
        ),
      });
    }

    baseColumns.push(
      {
        key: "department",
        label: "Department",
      },
      {
        key: "priority",
        label: "Priority",
        render: (ticket: SupportTicket) => (
          <TicketPriorityBadge priority={ticket.priority} />
        ),
      },
      {
        key: "createdAtDisplay",
        label: "Created At",
      },
      {
        key: "status",
        label: "Status",
        render: (ticket: SupportTicket) => (
          <TicketStatusBadge status={ticket.status} />
        ),
      },
      {
        key: "updatedAtDisplay",
        label: "Updated At",
      },
      {
        key: "manage",
        label: "Manage",
        filterable: false,
        render: (ticket: SupportTicket) => (
          <Link
            to={`${detailBasePath}/${ticket.id}`}
            className="ticket-view-btn"
            onClick={(event) => event.stopPropagation()}
          >
            <i className="fa fa-search" /> View
          </Link>
        ),
      },
    );

    return baseColumns;
  }, [detailBasePath, showSubscriber]);

  return (
    <div className="ticket-table-wrapper">
      <DataTable
        columns={columns}
        data={tableRows}
        getRowKey={(ticket) => ticket.id}
      />
    </div>
  );
};

export default TicketTable;
